import { useCallback, useContext, useEffect, useState } from 'react';
import { DocumentAccessProvider } from '@providers/DocumentAccess';
import { mixpanelTrack } from '@providers/Mixpanel';
import Spacer from '@components/Spacer';
import ProgressIndicator from '@components/ProgressIndicator';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as ApiModels from '@typings/api-models';
import UserWorkflowInfo from '@components/UserWorkflowInfo';
import { useLocalStorageState } from '@providers/LocalStorageState';
import FieldError from '@components/FieldError';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import LayoutRightPanel from '@layouts/MainPanelSubLayout/LayoutRightPanel';
import UserWorkflowDiscussion from '@lib/Creators/Dashboard/Workflows/UserWorkflowWidgets/UserWorkflowDiscussion';
import UserWorkflowInvoices from '@lib/Creators/Dashboard/Workflows/UserWorkflowInvoices';
import { UserWorkflowInvoice } from '@noodleApi/ApiDefinition/workflows';
import { useIsMobile } from '@hooks';
import { SIZES } from '@styles/media';
import useSocketContext from '@providers/Socket/useSocketContext';
import { SocketMessageType } from '@providers/Socket/SocketContext';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import { isAttorney } from '@helpers/businessCategories';
import { getUserWorkflowById } from '@tsClient/workflows';
import Switch from '@components/DesignLibrary/Atoms/Switch';
import { UserWorkflowContextProvider, type WorkflowCollaborator } from '@/hooks/useUserWorkflowContext';
import UserWorkflowSteps from './UserWorkflowSteps';
import s from './UserWorkflowDetailCustomerView.module.scss';
import OwnerInfo from './OwnerInfo';
import useLocalStorageStateSerializers from './useLocalStorageStateSerializers';

type Props = {
  userWorkflowId: string;
};

export type ThisUserWorkflow = Awaited<ReturnType<typeof getUserWorkflowById>>;

export type ThisCollaborator = WorkflowCollaborator;

const UserWorkflowDetailCustomerView: React.FC<Props> = ({ userWorkflowId }) => {
  const [showAssignedToMe, setShowAssignedToMe] = useState(false);

  const { setBrowserTabTitle: setBrowserTabTitleProfile, isInUserProfile, creatorSlug } = useContext(UserProfileContext);

  const [userWorkflow, setUserWorkflow] = useLocalStorageState<ThisUserWorkflow | null>({
    defaultValue: null,
    key: 'userWorkflow',
    prefix: 'workflows',
    ...useLocalStorageStateSerializers.userWorkflowDetails,
  });
  const [collaborators, setCollaborators] = useLocalStorageState<ThisCollaborator[]>({
    defaultValue: [],
    key: 'collaborators',
    prefix: 'workflows',
    ...useLocalStorageStateSerializers.collaborators,
  });

  const [invoices, setInvoices] = useState<UserWorkflowInvoice[]>([]);

  const [compiledDocuments, setCompiledDocuments] = useState<Parameters<typeof UserWorkflowInfo>[0]['compiledDocuments']>([]);
  const { setBrowserTabTitle } = useContext(DashboardContext);

  const { addListener, removeListener, isInitialized: isSocketInitialized, joinChannels } = useSocketContext();

  const {
    data: loadedUserWorkflow,
    error: loadUserWorkflowError,
    getData: getUserWorkflowFn,
  } = useNoodleApi(tsClient.workflows.getUserWorkflowById, { toastOnError: true });

  const {
    data: loadedCollaborators,
    error: loadCollaboratorsError,
    getData: getCollaboratorsFn,
  } = useNoodleApi(tsClient.workflows.getUserWorkflowCollaborators, { toastOnError: true });

  const {
    data: loadedInvoices,
    error: loadInvoicesError,
    getData: getInvoicesFn,
  } = useNoodleApi(tsClient.workflows.getUserWorkflowInvoices, { toastOnError: true });

  const { data: loadedCreator, getData: getCreatorFn } = useNoodleApi(tsClient.getCreator, { toastOnError: true });

  const { data: loadedCompiledDocumentsResponse, getData: getUserWorkflowDocuments } = useNoodleApi(
    tsClient.workflows.getUserWorkflowCompiledDocuments,
    { toastOnError: true },
  );

  const error = loadCollaboratorsError || loadUserWorkflowError || loadInvoicesError;
  const isMobile = useIsMobile(SIZES.xl);

  useEffect(() => {
    mixpanelTrack('Workflow - viewed user workflow details (Customer view)', { userWorkflowId });
  }, [userWorkflowId]);

  useEffect(() => {
    if (userWorkflowId) {
      getUserWorkflowFn({ userWorkflowId });
      getCollaboratorsFn({ userWorkflowId });
      getUserWorkflowDocuments({ perPage: 1000, userWorkflowId });
      getInvoicesFn({ userWorkflowId });
      if (creatorSlug) {
        getCreatorFn({ creatorSlug });
      }
    }
  }, [userWorkflowId, getUserWorkflowFn, getCollaboratorsFn, getUserWorkflowDocuments, getInvoicesFn, creatorSlug, getCreatorFn, isMobile]);

  useEffect(() => {
    if (loadedUserWorkflow) {
      setUserWorkflow(loadedUserWorkflow);
    }
  }, [loadedUserWorkflow, setUserWorkflow]);

  useEffect(() => {
    if (loadedCollaborators) {
      setCollaborators(loadedCollaborators);
    }
  }, [loadedCollaborators, setCollaborators]);

  useEffect(() => {
    if (loadedInvoices) {
      const filteredInvoices = (loadedInvoices || []).filter(invoice => invoice.status !== ApiModels.INVOICE_STATUS.EDITED);
      setInvoices(filteredInvoices);
    }
  }, [loadedInvoices, setInvoices]);

  useEffect(() => {
    if (loadedCompiledDocumentsResponse) {
      setCompiledDocuments(loadedCompiledDocumentsResponse.items);
    }
  }, [loadedCompiledDocumentsResponse, setUserWorkflow]);

  useEffect(() => {
    if (error) {
      setUserWorkflow(null);
      setCollaborators([]);
      setInvoices([]);
    }
  }, [error, setUserWorkflow, setCollaborators, setInvoices]);

  useEffect(() => {
    if (userWorkflow) {
      if (isInUserProfile) {
        setBrowserTabTitleProfile(userWorkflow.workflow.name);
      } else {
        setBrowserTabTitle(userWorkflow.workflow.name);
      }
    }
  }, [userWorkflow, isInUserProfile, setBrowserTabTitle, setBrowserTabTitleProfile]);

  const refetchWorkflow = useCallback(async () => {
    if (userWorkflowId) {
      await getCollaboratorsFn({ userWorkflowId });
      await getUserWorkflowFn({ userWorkflowId });
      await getInvoicesFn({ userWorkflowId });
    }
  }, [userWorkflowId, getUserWorkflowFn, getCollaboratorsFn, getInvoicesFn]);

  useEffect(() => {
    if (isSocketInitialized && userWorkflowId) {
      joinChannels([userWorkflowId]);
    }
  }, [isSocketInitialized, joinChannels, userWorkflowId]);

  useEffect(() => {
    setUserWorkflow(null);
    setCollaborators([]);
  }, [userWorkflowId, setUserWorkflow, setCollaborators]);

  useEffect(() => {
    if (isSocketInitialized) {
      const listenerId = addListener({
        fn: refetchWorkflow,
        messageType: SocketMessageType.USER_WORKFLOW_UPDATED,
      });
      return () => {
        removeListener(listenerId);
      };
    }
    return () => {};
  }, [addListener, removeListener, refetchWorkflow, isSocketInitialized]);

  if (error) {
    return (
      <div className={s.userWorkflowDetail}>
        <FieldError>Error: {error.message}</FieldError>
      </div>
    );
  }

  if (!userWorkflow || !collaborators) {
    return (
      <>
        <Spacer size={48} />
        <ProgressIndicator isCentered />
      </>
    );
  }

  return (
    <DocumentAccessProvider>
      <UserWorkflowContextProvider collaborators={collaborators}>
        <div className={s.userWorkflowDetail}>
          <div className={s.userWorkflowDetail__wrapper}>
            <div className={s.topBar}>
              <Switch
                label="My to-do list"
                name="assigned-to-me"
                onChange={() => setShowAssignedToMe(!showAssignedToMe)}
                isChecked={showAssignedToMe}
              />
              {isMobile && loadedCreator && (
                <LayoutRightPanel isInUserProfile isAttorney={isAttorney(loadedCreator.businessCategory)}>
                  <UserWorkflowDiscussion isInWorkflow isLoaded userWorkflow={userWorkflow} />
                </LayoutRightPanel>
              )}
            </div>
            <OwnerInfo owner={userWorkflow.person} isCreatorView={false} userWorkflow={userWorkflow} />
            <UserWorkflowInfo
              creator={loadedCreator}
              compiledDocuments={compiledDocuments}
              userWorkflow={userWorkflow}
              hasBorder={false}
              collaborators={collaborators}
              workflowSteps={userWorkflow.workflow.sortedSteps}
              isCreatorView={false}
            />
            <UserWorkflowInvoices
              canAssignCollaborators={false}
              creator={userWorkflow.workflow.creator}
              invoices={invoices}
              isCreatorView={false}
              userWorkflowId={userWorkflowId}
              onChange={refetchWorkflow}
            />
            <UserWorkflowSteps
              isWorkflowComplete={userWorkflow.numCompletedTasks === userWorkflow.numTotalTasks}
              userWorkflowId={userWorkflowId}
              onChange={refetchWorkflow}
              customerId={userWorkflow.person.id}
              creator={userWorkflow.workflow.creator}
              workflowSteps={userWorkflow.workflow.sortedSteps}
              assignedToMeOnly={showAssignedToMe}
            />
            <Spacer size={24} />
          </div>
          {!isMobile && (
            <LayoutRightPanel isInUserProfile>
              <UserWorkflowDiscussion isInWorkflow isLoaded userWorkflow={userWorkflow} />
            </LayoutRightPanel>
          )}
        </div>
      </UserWorkflowContextProvider>
    </DocumentAccessProvider>
  );
};

export default UserWorkflowDetailCustomerView;
